import type { Page } from '@micarahr/api-client';
import { apiTransport } from './api-client';
import type { Page as PagedResult } from './collections';
import type { AdminEmployee, DirectoryScope, EmployeeFilter, EmployeeSummary } from './directory';

/**
 * The employee directory, against the API.
 *
 * The important change from the prototype is not the data source — it is that
 * filtering and paging move to the server. The prototype read every employee
 * into the browser and filtered the array; at forty demonstration records that
 * is fine and at four thousand it is a slow page that also happens to have put
 * the entire workforce in memory, including people the viewer's scope does not
 * cover.
 *
 * Scope is applied in the query rather than to the results, and it is applied
 * by the server. A client that fetches everything and hides some of it has not
 * restricted anything: the data was already delivered.
 */

/**
 * Cursor paging behind a page-numbered UI.
 *
 * The API pages by cursor because a cursor is stable while rows are being
 * inserted; the approved directory has numbered pages. Rather than change the
 * approved UI, cursors are remembered per filter as the reader moves forward,
 * so Next and Previous are exact and a jump to an unvisited page falls back to
 * the nearest known cursor.
 */
const cursorCache = new Map<string, string[]>();

function cacheKey(filter: EmployeeFilter, scope: DirectoryScope): string {
  return JSON.stringify([
    filter.search ?? '',
    filter.departmentId ?? '',
    filter.employmentStatus ?? '',
    filter.pageSize ?? 25,
    scope.departmentIds,
  ]);
}

function toPage<T>(page: Page<T>, pageNumber: number, pageSize: number): PagedResult<T> {
  // `filteredCount`, not `totalCount`: this endpoint's total is everyone the
  // caller may see, so a search or a status filter showed the unfiltered count
  // and paged past the end of its own results.
  return { rows: page.items, total: page.filteredCount, page: pageNumber, pageSize };
}

/** What the directory endpoint actually returns: relations nested, not flattened. */
interface ApiEmployee {
  id: string;
  employeeNumber: string;
  fullName: string;
  preferredName: string | null;
  workEmail: string;
  employmentStatus: string;
  employmentType: string;
  dateHired: string;
  /** The last working day, when a separation is on record (case first). */
  separationDate?: string | null;
  department: { id: string; name: string } | null;
  team: { id: string; name: string } | null;
  position: { id: string; title: string } | null;
  workLocation: { id: string; name: string } | null;
  manager: { id: string; fullName: string } | null;
}

/**
 * Map the API's employee onto the frontend's.
 *
 * Written out rather than cast. The two shapes differ in a way a cast would
 * hide completely: the API nests relations (`department: {id, name}`) and the
 * screens read flat fields (`departmentId`, `departmentName`). A cast compiles,
 * renders `undefined` in every department cell, and looks like missing data
 * rather than a mapping bug.
 *
 * Fields the directory endpoint deliberately does not return — birth date,
 * address, personal email, government identifiers — are empty here rather than
 * absent, because the screens type them as strings. They are governed by
 * `employees.view_sensitive` and arrive from the detail endpoint, for a caller
 * who holds it.
 */
function toAdminEmployee(row: ApiEmployee): AdminEmployee {
  return {
    id: row.id,
    employeeNumber: row.employeeNumber,
    fullName: row.fullName,
    preferredName: row.preferredName ?? '',
    workEmail: row.workEmail,
    employmentStatus: row.employmentStatus as AdminEmployee['employmentStatus'],
    employmentType: row.employmentType as AdminEmployee['employmentType'],
    dateHired: row.dateHired,
    departmentId: row.department?.id ?? '',
    departmentName: row.department?.name ?? '',
    teamId: row.team?.id ?? '',
    teamName: row.team?.name ?? '',
    positionId: row.position?.id ?? '',
    positionTitle: row.position?.title ?? '',
    workLocationId: row.workLocation?.id ?? '',
    workLocationName: row.workLocation?.name ?? '',
    managerEmployeeId: row.manager?.id ?? null,
    managerName: row.manager?.fullName ?? null,
    // Not returned by the directory endpoint; the panel reads the detail
    // endpoint (see toAdminEmployeeDetail). Empty rather than absent, so a
    // screen that reads a list row never meets undefined.
    probationEndDate: null,
    contractEndDate: null,
    // "Separating" in the list until the last working day, "Separated" after.
    separationDate: row.separationDate?.slice(0, 10) ?? null,
    costCenterId: '',
    shiftId: '',
    monthlyRate: 0,
    sssNumber: '',
    philHealthNumber: '',
    pagIbigNumber: '',
    tinNumber: '',
    bankAccount: '',
    emergencyContactName: '',
    emergencyContactNumber: '',
    emergencyContactRelation: '',
    dependents: [],
    avatarUrl: null,
    hasLogin: null,
    firstName: '',
    middleName: '',
    lastName: '',
    personalEmail: '',
    mobileNumber: '',
    address: '',
    birthDate: '',
    gender: '',
    civilStatus: '',
    jobLevelId: '',
    jobLevelName: '',
  };
}

/** What `GET /employees/:id` returns: the directory fields plus the profile. */
interface ApiEmployeeDetail extends ApiEmployee {
  firstName: string;
  middleName: string | null;
  lastName: string;
  personalEmail: string | null;
  mobileNumber: string | null;
  probationEndDate: string | null;
  regularSince?: string | null;
  contractEndDate: string | null;
  separationDate: string | null;
  jobLevel: { id: string; name: string } | null;
  payrollGroup: { id: string; name: string } | null;
  costCenter: { id: string; name: string } | null;
  shiftTemplate: { id: string; name: string } | null;
  account: { status: string } | null;
  personal: {
    birthDate: string | null;
    gender: string | null;
    civilStatus: string | null;
    address: string | null;
    governmentReferences?: {
      sss: string | null;
      philHealth: string | null;
      pagIbig: string | null;
      tin: string | null;
    };
    bankAccount?: string | null;
  } | null;
  emergencyContacts: { name: string; relationship: string; mobileNumber: string }[];
  dependents: { name: string; relationship: string; birthDate: string | null }[];
}

/**
 * The full record, for the employee panel.
 *
 * The panel used to render the directory row, which carries none of this —
 * `dependents` was undefined and the Personal tab crashed the application for
 * every employee. Everything the detail endpoint returns is mapped; what it
 * withholds from this caller stays empty, and the panel says so.
 */
function toAdminEmployeeDetail(row: ApiEmployeeDetail): AdminEmployee {
  const base = toAdminEmployee(row);
  const contact = row.emergencyContacts[0];
  const references = row.personal?.governmentReferences;
  return {
    ...base,
    firstName: row.firstName,
    middleName: row.middleName ?? '',
    lastName: row.lastName,
    personalEmail: row.personalEmail ?? '',
    mobileNumber: row.mobileNumber ?? '',
    address: row.personal?.address ?? '',
    birthDate: row.personal?.birthDate?.slice(0, 10) ?? '',
    gender: row.personal?.gender ?? '',
    civilStatus: row.personal?.civilStatus ?? '',
    jobLevelId: row.jobLevel?.id ?? '',
    jobLevelName: row.jobLevel?.name ?? '',
    probationEndDate: row.probationEndDate?.slice(0, 10) ?? null,
    regularSince: row.regularSince?.slice(0, 10) ?? null,
    contractEndDate: row.contractEndDate?.slice(0, 10) ?? null,
    separationDate: row.separationDate?.slice(0, 10) ?? null,
    costCenterId: row.costCenter?.id ?? '',
    costCenterName: row.costCenter?.name ?? null,
    shiftId: row.shiftTemplate?.id ?? '',
    shiftName: row.shiftTemplate?.name ?? null,
    payrollGroupId: row.payrollGroup?.id ?? null,
    payrollGroupName: row.payrollGroup?.name ?? null,
    hasLogin: row.account !== null,
    sssNumber: references?.sss ?? '',
    philHealthNumber: references?.philHealth ?? '',
    pagIbigNumber: references?.pagIbig ?? '',
    tinNumber: references?.tin ?? '',
    bankAccount: row.personal?.bankAccount ?? '',
    emergencyContactName: contact?.name ?? '',
    emergencyContactRelation: contact?.relationship ?? '',
    emergencyContactNumber: contact?.mobileNumber ?? '',
    dependents: row.dependents.map((dependent) => ({
      name: dependent.name,
      relation: dependent.relationship,
      birthDate: dependent.birthDate?.slice(0, 10) ?? '',
    })),
  };
}

/**
 * The rail's counts for someone without `report.view`.
 *
 * A manager or recruiter may open the directory but not the workforce report,
 * which counts the whole company. Counting from the directory itself gives the
 * figures for exactly the people this person can see — their team, for a
 * manager — which is what the rail above their table should say anyway. One
 * single-row request per status: the server's total is the count.
 */
async function countVisibleEmployees(): Promise<WorkforceCounts> {
  const total = async (query: Record<string, string | undefined>) =>
    (
      await apiTransport().page<unknown>('/api/v1/employees', {
        query: { ...query, limit: 1 },
      })
    ).filteredCount;

  const statuses = ['regular', 'probationary', 'on_leave', 'inactive', 'suspended'];
  const [headcount, newHiresThisMonth, separating, separated, notStarted, ...counts] =
    await Promise.all([
      total({ workforce: 'current' }),
      total({ workforce: 'new_this_month' }),
      total({ workforce: 'separating' }),
      total({ workforce: 'separated' }),
      total({ workforce: 'upcoming' }),
      ...statuses.map((status) => total({ workforce: 'staying', employmentStatus: status })),
    ]);
  return {
    headcount,
    newHiresThisMonth,
    separating,
    separated,
    notStarted,
    byStatus: statuses.map((status, index) => ({ status, count: counts[index] ?? 0 })),
  };
}

interface WorkforceCounts {
  headcount: number;
  byStatus: { status: string; count: number }[];
  newHiresThisMonth?: number;
  separating?: number;
  separated?: number;
  notStarted?: number;
}

/**
 * The Status filter, in the server's terms.
 *
 * A single status is counted within the current workforce less anybody
 * separating, as the rail counts it, so "Regular" in the filter lists the
 * people the "Regular" stat counted — and none of them wears a "Separating —
 * leaving on …" badge. Separating and separated are dates, not statuses: the
 * server's `workforce` groups answer them by the rule the badges use.
 * "Inactive" is inactive or suspended, like the stat.
 */
function statusQuery(status: EmployeeFilter['employmentStatus']): Record<string, string> {
  switch (status) {
    case undefined:
    case 'all':
      return {};
    case 'current':
      return { workforce: 'current' };
    case 'upcoming':
      return { workforce: 'upcoming' };
    case 'separating':
      return { workforce: 'separating' };
    case 'separated':
      return { workforce: 'separated' };
    // The prototype's word for a confirmed employee; the server's is `regular`.
    case 'active':
    case 'regular':
      return { workforce: 'staying', employmentStatus: 'regular' };
    case 'inactive':
      return { workforce: 'staying', employmentStatus: 'inactive,suspended' };
    case 'resigned':
      return { employmentStatus: 'resigned' };
    default:
      return { workforce: 'staying', employmentStatus: status };
  }
}

export const apiEmployeeService = {
  /**
   * The rail above the directory, counted where the rows are.
   *
   * This method was missing from the adapter, so the selector's spread kept
   * the prototype's — and the screen showed a fictional 124 employees above a
   * table of 41 real ones. The same names-beside-invented-numbers failure as
   * the organization page, arrived at through a different door: a spread
   * cannot tell an unimplemented method from an implemented one.
   */
  async summary(scope?: DirectoryScope): Promise<EmployeeSummary> {
    let report: WorkforceCounts;
    if (scope?.reportsAllowed === false) {
      report = await countVisibleEmployees();
    } else {
      try {
        report = await apiTransport().get('/api/v1/reports/workforce');
      } catch (error) {
        if ((error as { status?: number }).status !== 403) throw error;
        report = await countVisibleEmployees();
      }
    }

    const count = (status: string) =>
      report.byStatus.find((row) => row.status === status)?.count ?? 0;

    /*
     * The current workforce: started and not past their last working day.
     * `byStatus` counts those of them staying and `separating` the rest, so
     * the rail's figures add up; people past their last day are the separate
     * `separated` figure.
     */
    return {
      total: report.headcount,
      // The server's employment vocabulary calls a confirmed employee
      // `regular` — "active" is the prototype's word for the same fact.
      active: count('regular'),
      probationary: count('probationary'),
      onLeave: count('on_leave'),
      // Within the current workforce, as the Status filter's "Inactive" is.
      // People who have left are their own figure, so no stat counts anybody
      // the default list (the current workforce) leaves out.
      inactive: count('inactive') + count('suspended'),
      separating: report.separating ?? 0,
      separated: report.separated ?? 0,
      notStarted: report.notStarted ?? 0,
      newHiresThisMonth: report.newHiresThisMonth ?? null,
    };
  },

  async list(filter: EmployeeFilter, scope: DirectoryScope): Promise<PagedResult<AdminEmployee>> {
    const pageSize = filter.pageSize ?? 25;
    const pageNumber = filter.page ?? 1;
    const key = cacheKey(filter, scope);

    const cursors = cursorCache.get(key) ?? [];
    // Index 0 is the first page, which needs no cursor. A jump beyond what has
    // been walked uses the furthest known cursor rather than failing.
    const cursor =
      pageNumber <= 1 ? undefined : cursors[Math.min(pageNumber - 2, cursors.length - 1)];

    const page = await apiTransport().page<ApiEmployee>('/api/v1/employees', {
      query: {
        search: filter.search,
        departmentId: filter.departmentId,
        ...statusQuery(filter.employmentStatus),
        limit: pageSize,
        cursor,
      },
    });

    if (page.pageInfo.nextCursor) {
      const next = [...cursors];
      next[pageNumber - 1] = page.pageInfo.nextCursor;
      cursorCache.set(key, next);
    }

    return toPage({ ...page, items: page.items.map(toAdminEmployee) }, pageNumber, pageSize);
  },

  async get(id: string): Promise<AdminEmployee | null> {
    try {
      return toAdminEmployeeDetail(
        await apiTransport().get<ApiEmployeeDetail>(`/api/v1/employees/${id}`),
      );
    } catch (error) {
      // A record outside the viewer's scope answers 404 rather than 403, and
      // deliberately so: telling somebody a record exists but is not theirs to
      // see is itself a disclosure.
      const status = (error as { status?: number }).status;
      if (status === 404) return null;
      throw error;
    }
  },

  create(input: Record<string, unknown>): Promise<AdminEmployee> {
    return apiTransport().post<AdminEmployee>('/api/v1/employees', input);
  },

  update(id: string, input: Record<string, unknown>): Promise<AdminEmployee> {
    return apiTransport().patch<AdminEmployee>(`/api/v1/employees/${id}`, input);
  },

  /**
   * Named to match the mock's `changeEmploymentStatus`, because the service the
   * screens see is `{...mock, ...api}` — under the old name `changeStatus` this
   * method was unreachable and the mock silently faked a success in API mode.
   *
   * The server has no POST /employees/:id/change-status route yet (the
   * employees module is read-only), so this call 404s. That is intended: an
   * honest failure the screen reports beats a fake success nobody notices.
   */
  changeEmploymentStatus(
    id: string,
    status: AdminEmployee['employmentStatus'],
    reason: string,
    _actor: unknown,
  ): Promise<AdminEmployee> {
    return apiTransport().post<AdminEmployee>(`/api/v1/employees/${id}/change-status`, {
      status,
      reason,
    });
  },

  /** Clears the cursor cache. Called when filters change materially. */
  resetPaging(): void {
    cursorCache.clear();
  },
};
